import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    Button,
    Badge
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Plus, Trash2, Disc, Tool, Clipboard, X, FileText } from "react-feather";
import { useConfig } from "../../Context/ConfigContext";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────
interface FormValues {
    branch_id: string;
    vehicle_id: string;
    service_date: string;
    odometer_reading: string;
    next_service_date: string;
    status: string;
    total_cost?: string;
    invoice_reference?: string;
    invoice_document?: FileList;
    description?: string;
    parts_vendor_name?: string;
    parts_vendor_details?: string;
    parts_replaced: { name: string; amount: string }[];
    garage_name?: string;
    labour_vendor_details?: string;
    service_type?: string;
    labour_services: { item_description: string; unit_price: string }[];
}

interface InspectionState {
    inspection_date: string;
    inspection_type: string;
    inspection_status: string;
    vendor_name: string;
    vendor_details: string;
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function AddMaintenance() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [serviceTypes, setServiceTypes] = useState<any[]>([]);
    const [inspectionTypes, setInspectionTypes] = useState<any[]>([]);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [pendingMessage, setPendingMessage] = useState("");

    // Maintenance type toggle
    const [maintenanceType, setMaintenanceType] = useState<'parts' | 'labour' | 'both'>('parts');
    const [sameVendor, setSameVendor] = useState(false);

    // ── Inspection state ──────────────────────
    const [includeInspection, setIncludeInspection] = useState(false);
    const [inspection, setInspection] = useState<InspectionState>({
        inspection_date: "",
        inspection_type: "",
        inspection_status: "Pending",
        vendor_name: "",
        vendor_details: "",
    });
    const [inspectionFiles, setInspectionFiles] = useState<File[]>([]);
    const [inspectionErrors, setInspectionErrors] = useState<Record<string, string>>({});
    const inspectionFileRef = useRef<HTMLInputElement>(null);

    const updateInspection = (key: keyof InspectionState, value: string) => {
        setInspection(prev => ({ ...prev, [key]: value }));
        setInspectionErrors(prev => ({ ...prev, [key]: "" }));
    };


    const validateInspection = (): boolean => {
        const errs: Record<string, string> = {};
        if (!inspection.inspection_date) errs.inspection_date = "Inspection date is required";
        if (!inspection.inspection_type.trim()) errs.inspection_type = "Inspection type is required";
        if (!inspection.inspection_status) errs.inspection_status = "Status is required";
        if (!inspection.vendor_name.trim()) errs.vendor_name = "Vendor name is required";
        if (!inspection.vendor_details.trim()) errs.vendor_details = "Vendor details are required";
        if (inspectionFiles.length === 0) errs.report_files = "At least one report file is required";
        setInspectionErrors(errs);
        return Object.keys(errs).length === 0;
    };
    // ─────────────────────────────────────────

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        reset,
        register,
        trigger,
    } = useForm<FormValues>({
        mode: "onChange",
        defaultValues: {
            parts_replaced: [{ name: "", amount: "" }],
            labour_services: [{ item_description: "", unit_price: "" }],
            status: "Pending",
        },
    });
    const { config } = useConfig();
    const partsArray = useFieldArray({ control, name: "parts_replaced" });
    const labourArray = useFieldArray({ control, name: "labour_services" });

    const selectedBranch = watch("branch_id");
    const selectedVehicle = watch("vehicle_id");
    const watchedStatus = watch("status");
    const watchedServiceDate = watch("service_date");
    const watchedNextServiceDate = watch("next_service_date");
    const watchedParts = watch("parts_replaced");
    const watchedLabour = watch("labour_services");
    const partsVendorName = watch("parts_vendor_name");
    const partsVendorDetails = watch("parts_vendor_details");

    // Sync "Same as Parts" toggle
    useEffect(() => {
        if (maintenanceType === 'both' && sameVendor) {
            setValue("garage_name", partsVendorName, { shouldValidate: !!partsVendorName });
            setValue("labour_vendor_details", partsVendorDetails);
        }
    }, [sameVendor, partsVendorName, partsVendorDetails, maintenanceType, setValue, trigger]);

    // Auto-fill inspection branch/vehicle when main form changes (for display info only)
    // No binding needed since we read selectedBranch / selectedVehicle directly at submit time

    // Cost calculations — only include active sections based on maintenance type
    const partsTotal = (maintenanceType === 'parts' || maintenanceType === 'both')
        ? (watchedParts || []).reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
        : 0;
    const labourTotal = (maintenanceType === 'labour' || maintenanceType === 'both')
        ? (watchedLabour || []).reduce((sum, item) => sum + (Number(item.unit_price) || 0), 0)
        : 0;
    const grandTotal = (partsTotal + labourTotal).toFixed(2);

    useEffect(() => { setValue("total_cost", grandTotal); }, [grandTotal, setValue]);

    // Pending check
    useEffect(() => {
        if (!selectedVehicle) { setSubmissionBlocked(false); setPendingMessage(""); return; }
        const checkPending = async () => {
            try {
                const response: any = await apiPost("maintenance/check_pending", { vehicle_id: selectedVehicle });
                if (response?.exists) { setPendingMessage(response.message); setSubmissionBlocked(true); }
                else { setSubmissionBlocked(false); setPendingMessage(""); }
            } catch { setSubmissionBlocked(false); setPendingMessage(""); }
        };
        checkPending();
    }, [selectedVehicle]);

    // Branches & Master Data
    useEffect(() => {
        apiPost("branches/list", { status: 'Active' })
            .then((data: any) => setBranches(data))
            .catch(console.error);

        // Fetch Service Types
        apiPost('master-entries/list', { type: 'service_type' })
            .then((res: any) => setServiceTypes(Array.isArray(res) ? res : []))
            .catch(console.error);

        // Fetch Inspection Types
        apiPost('master-entries/list', { type: 'inspection_type' })
            .then((res: any) => setInspectionTypes(Array.isArray(res) ? res : []))
            .catch(console.error);
    }, []);

    // Vehicles
    useEffect(() => {
        if (!selectedBranch) { setVehicles([]); return; }
        apiPost("vehicles/list", { branch_id: selectedBranch })
            .then((response: any) => {
                const raw = Array.isArray(response) ? response : (response.data ?? []);
                setVehicles(raw.filter((v: any) => v.status !== 'Sold'));
            })
            .catch(() => setVehicles([]));
        setValue("vehicle_id", "");
    }, [selectedBranch, setValue]);

    const onInvalid = () => {
        if (includeInspection) {
            const isInspectionValid = validateInspection();
            // if (!isInspectionValid) {
            //     toast.error("Please fill in the required Inspection fields.");
            // }
        }
    };

    // ─────────────────────────────────────────
    // Submit
    // ─────────────────────────────────────────
    const onSubmit = async (data: FormValues) => {
        if (submissionBlocked) {
            toast.error(pendingMessage || "A pending maintenance record already exists for this vehicle.");
            return;
        }

        // Validate inspection section if enabled
        if (includeInspection && !validateInspection()) {
            // toast.error("Please fill in the required Inspection fields.");
            return;
        }

        setLoading(true);
        try {
            // Upload invoice
            let invoiceDocName = "";
            if (data.invoice_document && data.invoice_document.length > 0) {
                const file = data.invoice_document[0];
                if (file.size > 2 * 1024 * 1024) { toast.error("Invoice file must be less than 2 MB"); setLoading(false); return; }
                invoiceDocName = await uploadFile(file);
            }

            // Filter empty line items
            const parts = (data.parts_replaced || []).filter(n => n.name?.trim());
            const labour = (data.labour_services || []).filter(n => n.item_description?.trim());

            const payload: any = {
                ...data,
                invoice_document: invoiceDocName,
                total_cost: grandTotal,
            };

            if (maintenanceType === 'parts') {
                payload.labour_services = [];
                payload.garage_name = null;
                payload.labour_vendor_details = null;
                payload.service_type = data.service_type || "Parts Only";
                payload.parts_replaced = parts;
            } else if (maintenanceType === 'labour') {
                payload.parts_replaced = [];
                payload.parts_vendor_name = null;
                payload.parts_vendor_details = null;
                payload.labour_services = labour;
            } else {
                payload.parts_replaced = parts;
                payload.labour_services = labour;
            }

            // Build inspection_data if section is enabled
            if (includeInspection) {
                const reportFiles: string[] = [];
                for (const file of inspectionFiles) {
                    const fname = await uploadFile(file);
                    reportFiles.push(fname);
                }
                payload.inspection_data = {
                    inspection_date: inspection.inspection_date,
                    inspection_type: inspection.inspection_type,
                    inspection_status: inspection.inspection_status,
                    vendor_name: inspection.vendor_name,
                    vendor_details: inspection.vendor_details,
                    report_files: reportFiles,
                };
            } else {
                payload.inspection_data = null;
            }

            await apiPost("maintenance/store", payload);
            toast.success("Maintenance record added successfully", { autoClose: 1000 });
            setTimeout(() => navigate(`${process.env.PUBLIC_URL}/maintenance`), 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error adding maintenance");
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────
    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Maintenance" parent="Maintenance" title="Add Maintenance" />
            <Container fluid>

                {/* ── Maintenance Type & Inspection Selector ── */}
                <div className="d-flex justify-content-center align-items-center mb-3 gap-4 flex-wrap">
                    <div className="btn-group">
                        <Button color={maintenanceType === 'parts' ? 'primary' : 'outline-primary'} onClick={() => setMaintenanceType('parts')} active={maintenanceType === 'parts'}>
                            <Disc size={16} className="me-2" />Parts Only
                        </Button>
                        <Button color={maintenanceType === 'labour' ? 'primary' : 'outline-primary'} onClick={() => setMaintenanceType('labour')} active={maintenanceType === 'labour'}>
                            <Tool size={16} className="me-2" />Labour Only
                        </Button>
                        <Button color={maintenanceType === 'both' ? 'primary' : 'outline-primary'} onClick={() => setMaintenanceType('both')} active={maintenanceType === 'both'}>
                            <span className="me-2">⚡</span>Hybrid (Both)
                        </Button>
                    </div>

                    {/* ── Inspection Toggle ── */}
                    <div
                        className="d-flex align-items-center gap-2 px-4 py-2 rounded-pill border"
                        style={{
                            background: includeInspection ? 'linear-gradient(135deg,#e8f5e9,#f1f8e9)' : '#f8f9fa',
                            borderColor: includeInspection ? '#4caf50' : '#dee2e6',
                            cursor: 'pointer',
                            transition: 'all .25s',
                        }}
                        onClick={() => setIncludeInspection(v => !v)}
                    >
                        <Input
                            type="checkbox"
                            id="include_inspection_toggle"
                            checked={includeInspection}
                            onChange={() => setIncludeInspection(v => !v)}
                            style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#4caf50' }}
                            onClick={e => e.stopPropagation()}
                        />
                        <Clipboard size={18} color={includeInspection ? '#2e7d32' : '#6c757d'} />
                        <Label
                            htmlFor="include_inspection_toggle"
                            className="mb-0 fw-semibold"
                            style={{ cursor: 'pointer', color: includeInspection ? '#2e7d32' : '#495057', userSelect: 'none' }}
                        >
                            Include Inspection Report
                        </Label>
                        {includeInspection && <Badge color="success" pill>Active</Badge>}
                    </div>
                </div>

                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>

                            {/* ── Core Fields ── */}
                            <Row className="g-3">
                                <Col md={4}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller name="branch_id" control={control} rules={{ required: "Branch is required" }}
                                        render={({ field }) => (<>
                                            <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                <option value="">Select Branch</option>
                                                {branches.map((b: any) => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                                            </Input>
                                            <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Vehicle <span className="text-danger">*</span></Label>
                                    <Controller name="vehicle_id" control={control} rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (<>
                                            <Input type="select" {...field} invalid={!!errors.vehicle_id} disabled={!selectedBranch}>
                                                <option value="">Select Vehicle</option>
                                                {vehicles.map((v: any) => <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>)}
                                            </Input>
                                            <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Service Date <span className="text-danger">*</span></Label>
                                    <Controller name="service_date" control={control} rules={{ required: "Service Date is required" }}
                                        render={({ field }) => (<>
                                            <DatePicker
                                                className={`form-control ${errors.service_date ? "is-invalid" : ""}`}
                                                wrapperClassName="w-100"
                                                selected={field.value ? new Date(field.value) : null}
                                                onChange={(date) => {
                                                    const d = date ? formatDate(date) : "";
                                                    if (watchedNextServiceDate && d && new Date(d) > new Date(watchedNextServiceDate)) {
                                                        toast.error("Service date cannot be after Next Service date"); return;
                                                    }
                                                    field.onChange(d);
                                                }}
                                                placeholderText="YYYY-MM-DD" showMonthDropdown showYearDropdown dropdownMode="select"
                                            />
                                            <FormFeedback className="d-block">{errors.service_date?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Odometer Reading (km) <span className="text-danger">*</span></Label>
                                    <Controller name="odometer_reading" control={control} rules={{ required: "Odometer is required" }}
                                        render={({ field }) => (<>
                                            <Input type="number" {...field} placeholder="12000" invalid={!!errors.odometer_reading} autoComplete="off" />
                                            <FormFeedback>{errors.odometer_reading?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Category / Service Type <span className="text-danger">*</span></Label>
                                    <Controller name="service_type" control={control} rules={{ required: "Category / Service Type is required" }}
                                        render={({ field }) => (<>
                                            <Input type="select" {...field} invalid={!!errors.service_type}>
                                                <option value="">Select Service Type</option>
                                                {serviceTypes.map((t: any) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                            </Input>
                                            <FormFeedback>{errors.service_type?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Status <span className="text-danger">*</span></Label>
                                    <Controller name="status" control={control} rules={{ required: "Status is required" }}
                                        render={({ field }) => (<>
                                            <Input type="select" {...field} invalid={!!errors.status}>
                                                <option value="">Select Status</option>
                                                <option value="Pending">Pending</option>
                                                <option value="In Progress">In Progress</option>
                                                <option value="Completed">Completed</option>
                                            </Input>
                                            <FormFeedback>{errors.status?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                            </Row>

                            {/* Pending warning */}
                            {submissionBlocked && (
                                <div className="alert alert-warning mt-3 py-2 text-danger">
                                    <strong>⚠ Blocked:</strong> {pendingMessage}
                                </div>
                            )}

                            {/* ═══════════════════════════════════════════
                                  INSPECTION REPORT SECTION (optional)
                             ═══════════════════════════════════════════ */}
                            {includeInspection && (
                                <Card className="mb-4 shadow-sm mt-4" style={{ border: '2px solid #4caf50', borderRadius: 12 }}>
                                    <CardBody>
                                        <div className="d-flex align-items-center gap-2 mb-3">
                                            <Clipboard size={22} color="#2e7d32" />
                                            <H5 className="mb-0" style={{ color: '#2e7d32' }}>Inspection Details</H5>
                                        </div>

                                        {/* Info strip showing linked branch/vehicle */}
                                        {(selectedBranch || selectedVehicle) && (
                                            <div className="alert alert-success py-2 mb-3 small text-dark">
                                                <strong>Linked to:</strong>
                                                {selectedBranch && branches.find(b => String(b.id) === String(selectedBranch)) && (
                                                    <span className="ms-2">Branch — <strong>{branches.find(b => String(b.id) === String(selectedBranch))?.branch_name}</strong></span>
                                                )}
                                                {selectedVehicle && vehicles.find(v => String(v.id) === String(selectedVehicle)) && (
                                                    <span className="ms-2">· Vehicle — <strong>{vehicles.find(v => String(v.id) === String(selectedVehicle))?.registration_number}</strong></span>
                                                )}
                                            </div>
                                        )}

                                        <Row className="g-3">
                                            {/* Inspection Date */}
                                            <Col md={4}>
                                                <Label>Inspection Date <span className="text-danger">*</span></Label>
                                                <DatePicker
                                                    className={`form-control ${inspectionErrors.inspection_date ? "is-invalid" : ""}`}
                                                    wrapperClassName="w-100"
                                                    selected={inspection.inspection_date ? new Date(inspection.inspection_date) : null}
                                                    onChange={(date) => updateInspection("inspection_date", date ? date.toISOString().split('T')[0] : "")}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                {inspectionErrors.inspection_date && <div className="invalid-feedback d-block">{inspectionErrors.inspection_date}</div>}
                                            </Col>

                                            {/* Inspection Type */}
                                            <Col md={4}>
                                                <Label>Inspection Type <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="select"
                                                    value={inspection.inspection_type}
                                                    onChange={(e) => updateInspection("inspection_type", e.target.value)}
                                                    invalid={!!inspectionErrors.inspection_type}
                                                >
                                                    <option value="">Select Inspection Type</option>
                                                    {inspectionTypes.map((t: any) => <option key={t.id} value={t.name}>{t.name}</option>)}
                                                </Input>
                                                {inspectionErrors.inspection_type && <FormFeedback className="d-block">{inspectionErrors.inspection_type}</FormFeedback>}
                                            </Col>

                                            {/* Inspection Status */}
                                            <Col md={4}>
                                                <Label>Status <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="select"
                                                    value={inspection.inspection_status}
                                                    onChange={(e) => updateInspection("inspection_status", e.target.value)}
                                                    invalid={!!inspectionErrors.inspection_status}
                                                >
                                                    <option value="Pending">Pending</option>
                                                    <option value="In Progress">In Progress</option>
                                                    <option value="Passed">Passed</option>
                                                    <option value="Failed">Failed</option>
                                                </Input>
                                                {inspectionErrors.inspection_status && <FormFeedback className="d-block">{inspectionErrors.inspection_status}</FormFeedback>}
                                            </Col>

                                            {/* Vendor Name */}
                                            <Col md={6}>
                                                <Label>Inspection Vendor Name <span className="text-danger">*</span></Label>
                                                <Input
                                                    value={inspection.vendor_name}
                                                    onChange={(e) => updateInspection("vendor_name", e.target.value)}
                                                    placeholder="Inspector / Agency"
                                                    invalid={!!inspectionErrors.vendor_name}
                                                    autoComplete="off"
                                                />
                                                {inspectionErrors.vendor_name && <FormFeedback className="d-block">{inspectionErrors.vendor_name}</FormFeedback>}
                                            </Col>

                                            {/* Vendor Details */}
                                            <Col md={6}>
                                                <Label>Vendor Details <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="textarea"
                                                    rows={1}
                                                    value={inspection.vendor_details}
                                                    onChange={(e) => updateInspection("vendor_details", e.target.value)}
                                                    placeholder="Address / Contact"
                                                    invalid={!!inspectionErrors.vendor_details}
                                                    autoComplete="off"
                                                />
                                                {inspectionErrors.vendor_details && <FormFeedback className="d-block">{inspectionErrors.vendor_details}</FormFeedback>}
                                            </Col>

                                            {/* Multi-File Upload - Simplified like accident screen */}
                                            <Col md={12}>
                                                <Label>Inspection Report Files <span className="text-danger">*</span> <span className="text-muted small">(Multiple)</span></Label>
                                                <Input
                                                    type="file"
                                                    multiple
                                                    accept="image/*,.pdf"
                                                    invalid={!!inspectionErrors.report_files}
                                                    onChange={(e) => {
                                                        if (e.target.files) {
                                                            setInspectionFiles(Array.from(e.target.files));
                                                            setInspectionErrors(prev => ({ ...prev, report_files: '' }));
                                                        }
                                                    }}
                                                />
                                                {inspectionErrors.report_files && <FormFeedback className="d-block">{inspectionErrors.report_files}</FormFeedback>}
                                                {inspectionFiles.length > 0 && (
                                                    <div className="text-success mt-1 small">
                                                        {inspectionFiles.length} file(s) ready to upload.
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </CardBody>
                                </Card>
                            )}

                            {/* ── Parts / Labour Sections ── */}
                            <Row className="mt-4">
                                {/* PARTS SECTION */}
                                {(maintenanceType === 'parts' || maintenanceType === 'both') && (
                                    <Col md={maintenanceType === 'both' ? 6 : 12}>
                                        <Card className="border-top-primary shadow-sm">
                                            <CardBody>
                                                <div className="d-flex justify-content-between mb-3 align-items-center">
                                                    <H5 className="text-primary mb-0"><Disc className="me-2" size={20} />Parts Details</H5>
                                                    <Badge color="primary" pill className="fs-6">{partsTotal.toFixed(2)}</Badge>
                                                </div>
                                                <Row className="g-2 mb-2">
                                                    <Col md={12}>
                                                        <Label>Vendor Name <span className="text-danger">*</span></Label>
                                                        <Controller control={control} name="parts_vendor_name" rules={{ required: "Parts Vendor is required" }}
                                                            render={({ field }) => (<>
                                                                <Input {...field} placeholder="Supplier Name" invalid={!!errors.parts_vendor_name} autoComplete="off" />
                                                                <FormFeedback>{errors.parts_vendor_name?.message}</FormFeedback>
                                                            </>)}
                                                        />
                                                    </Col>
                                                    <Col md={12}>
                                                        <Label>Vendor Details</Label>
                                                        <Controller control={control} name="parts_vendor_details"
                                                            render={({ field }) => (
                                                                <Input type="textarea" rows={2} {...field} placeholder="Address / Contact" autoComplete="off" />
                                                            )}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Label className="fw-bold mt-6">Parts List <span className="text-danger">*</span></Label>
                                                {partsArray.fields.map((field, index) => (
                                                    <Row key={field.id} className="mb-2 g-2 align-items-center">
                                                        <Col xs={7}>
                                                            <Controller control={control} name={`parts_replaced.${index}.name`} rules={{ required: "Part name is required" }}
                                                                render={({ field }) => (<>
                                                                    <Input {...field} placeholder="Part Name" invalid={!!errors.parts_replaced?.[index]?.name} autoComplete="off" />
                                                                    {errors.parts_replaced?.[index]?.name && <FormFeedback className="d-block">{errors.parts_replaced[index]?.name?.message}</FormFeedback>}
                                                                </>)}
                                                            />
                                                        </Col>
                                                        <Col xs={4}>
                                                            <Controller control={control} name={`parts_replaced.${index}.amount`}
                                                                rules={{ required: "Price is required", min: { value: 0.01, message: "Price > 0" } }}
                                                                render={({ field }) => (<>
                                                                    <Input type="number" {...field} placeholder={`Price (${config.currency})`} invalid={!!errors.parts_replaced?.[index]?.amount} autoComplete="off" />
                                                                    {errors.parts_replaced?.[index]?.amount && <FormFeedback className="d-block">{errors.parts_replaced[index]?.amount?.message}</FormFeedback>}
                                                                </>)}
                                                            />
                                                        </Col>
                                                        <Col xs={1}>
                                                            {partsArray.fields.length > 1 && <Trash2 size={16} className="text-danger cursor-pointer" onClick={() => partsArray.remove(index)} />}
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Button size="sm" color="light" className="w-100 text-primary" type="button" onClick={() => partsArray.append({ name: "", amount: "" })}>
                                                    <Plus size={14} className="me-1" /> Add Part
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                )}

                                {/* LABOUR SECTION */}
                                {(maintenanceType === 'labour' || maintenanceType === 'both') && (
                                    <Col md={maintenanceType === 'both' ? 6 : 12}>
                                        <Card className="border-top-warning shadow-sm">
                                            <CardBody>
                                                <div className="d-flex justify-content-between mb-3 align-items-center">
                                                    <H5 className="text-warning mb-0"><Tool className="me-2" size={20} />Labour Details</H5>
                                                    <div className="d-flex align-items-center gap-2">
                                                        {maintenanceType === 'both' && (
                                                            <div className="form-check form-switch mb-0">
                                                                <Input type="checkbox" className="form-check-input" id="sameVendorSwitch" checked={sameVendor} onChange={(e) => setSameVendor(e.target.checked)}
                                                                    style={{ transform: 'scale(1.2)', borderColor: sameVendor ? '#198754' : '', backgroundColor: sameVendor ? '#198754' : '' }} />
                                                                <Label className="ms-2 text-success fw-bold" htmlFor="sameVendorSwitch">Same as Parts</Label>
                                                            </div>
                                                        )}
                                                        <Badge color="warning" pill className="fs-6 text-dark">{labourTotal.toFixed(2)}</Badge>
                                                    </div>
                                                </div>
                                                <Row className="g-2 mb-2">
                                                    <Col md={12}>
                                                        <Label>Garage / Service Center <span className="text-danger">*</span></Label>
                                                        <Controller control={control} name="garage_name" rules={{ required: "Garage Name is required" }}
                                                            render={({ field }) => (<>
                                                                <Input {...field} placeholder="Garage Name" invalid={!!errors.garage_name} disabled={sameVendor && maintenanceType === 'both'} autoComplete="off" />
                                                                <FormFeedback>{errors.garage_name?.message}</FormFeedback>
                                                            </>)}
                                                        />
                                                    </Col>
                                                    <Col md={12}>
                                                        <Label>Vendor Details</Label>
                                                        <Controller control={control} name="labour_vendor_details"
                                                            render={({ field }) => (
                                                                <Input type="textarea" rows={2} {...field} placeholder="Address / Contact" disabled={sameVendor && maintenanceType === 'both'} autoComplete="off" />
                                                            )}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Label className="fw-bold ">Services List <span className="text-danger">*</span></Label>
                                                {labourArray.fields.map((field, index) => (
                                                    <Row key={field.id} className="mb-2 g-2 align-items-center">
                                                        <Col xs={7}>
                                                            <Controller control={control} name={`labour_services.${index}.item_description`} rules={{ required: "Description is required" }}
                                                                render={({ field }) => (<>
                                                                    <Input {...field} placeholder="Service Desc." invalid={!!errors.labour_services?.[index]?.item_description} autoComplete="off" />
                                                                    {errors.labour_services?.[index]?.item_description && <FormFeedback className="d-block">{errors.labour_services[index]?.item_description?.message}</FormFeedback>}
                                                                </>)}
                                                            />
                                                        </Col>
                                                        <Col xs={4}>
                                                            <Controller control={control} name={`labour_services.${index}.unit_price`}
                                                                rules={{ required: "Cost is required", min: { value: 0.01, message: "Cost > 0" } }}
                                                                render={({ field }) => (<>
                                                                    <Input type="number" {...field} placeholder={`Cost (${config.currency})`} invalid={!!errors.labour_services?.[index]?.unit_price} autoComplete="off" />
                                                                    {errors.labour_services?.[index]?.unit_price && <FormFeedback className="d-block">{errors.labour_services[index]?.unit_price?.message}</FormFeedback>}
                                                                </>)}
                                                            />
                                                        </Col>
                                                        <Col xs={1}>
                                                            {labourArray.fields.length > 1 && <Trash2 size={16} className="text-danger cursor-pointer" onClick={() => labourArray.remove(index)} />}
                                                        </Col>
                                                    </Row>
                                                ))}
                                                <Button size="sm" color="light" className="w-100 text-warning" type="button" onClick={() => labourArray.append({ item_description: "", unit_price: "" })}>
                                                    <Plus size={14} className="me-1" /> Add Service
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                )}
                            </Row>

                            {/* ── Bottom Fields ── */}
                            <Row className="mt-4">
                                <Col md={4}>
                                    <Label>Total Cost (Auto-Calc){watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Input value={grandTotal} readOnly className="bg-white fw-bold" />
                                    <input type="hidden" {...register("total_cost")} />
                                </Col>
                                <Col md={4}>
                                    <Label>Invoice Reference{watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller name="invoice_reference" control={control}
                                        rules={{ required: watchedStatus === 'Completed' ? "Invoice Reference is required" : false }}
                                        render={({ field }) => (<>
                                            <Input {...field} placeholder="INV-001" invalid={!!errors.invoice_reference} autoComplete="off" />
                                            <FormFeedback>{errors.invoice_reference?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Upload Invoice{watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller name="invoice_document" control={control}
                                        rules={{ required: watchedStatus === 'Completed' ? "Invoice Document is required" : false }}
                                        render={({ field: { value, onChange, ...field } }) => (<>
                                            <Input type="file" {...field} accept="image/*,.pdf"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files.length > 0) onChange(e.target.files);
                                                    else onChange(null);
                                                }}
                                                invalid={!!errors.invoice_document}
                                            />
                                            <FormFeedback>{errors.invoice_document?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={6} style={{ marginTop: "15px" }}>
                                    <Label>Next Service Due{watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller name="next_service_date" control={control}
                                        rules={{ required: watchedStatus === 'Completed' ? "Next Service Date is required" : false }}
                                        render={({ field }) => (<>
                                            <DatePicker
                                                className={`form-control ${errors.next_service_date ? "is-invalid" : ""}`}
                                                wrapperClassName="w-100"
                                                selected={field.value ? new Date(field.value) : null}
                                                onChange={(date) => {
                                                    const d = date ? formatDate(date) : "";
                                                    if (watchedServiceDate && d && new Date(d) < new Date(watchedServiceDate)) {
                                                        toast.error("Next Service date cannot be before Service date"); return;
                                                    }
                                                    field.onChange(d);
                                                }}
                                                placeholderText="YYYY-MM-DD" showMonthDropdown showYearDropdown dropdownMode="select"
                                            />
                                            <FormFeedback className="d-block">{errors.next_service_date?.message}</FormFeedback>
                                        </>)}
                                    />
                                </Col>
                                <Col md={6} style={{ marginTop: "15px" }}>
                                    <Label>Description / Notes</Label>
                                    <Input type="textarea" {...register("description")} placeholder="Add notes..." />
                                </Col>
                            </Row>

                            <div className="mt-4">
                                <Btn color="primary" disabled={loading} type="submit">
                                    {loading ? <> Submitting <Spinner size="sm" /></> : "Submit"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
